import { Environment } from '../env';
import { S3Operation } from '@unternehmensverzeichnis/shared/s3-operations';
import { splitAtLastOccurrence } from '@utils/split-at-last-occurrence';
import axios from 'axios';

export type AwsFile = {
    id: string;
    name: string;
    size: number;
    url: string;
};

export type PresignedUrlResponse = {
    url: string;
    fileId?: string;
    s3Url?: string;
};

class AwsS3PresignedClient {
    private token: string | null = null;

    setToken(token: string) {
        this.token = token;
    }

    private getApiEndpoint(): string {
        return `${Environment.BACKEND_BASE_URL}/s3-presigned-url`;
    }

    private checkToken(): void {
        if (!this.token) {
            throw new Error(
                'Authentication token is missing. Please set token before using this client.',
            );
        }
    }

    async getUploadUrl(file: File): Promise<AwsFile | undefined> {
        this.checkToken();
        console.debug(`Getting presigned URL for upload: ${file.name}`);

        try {
            // Get presigned URL for upload
            const response = await axios.post<PresignedUrlResponse>(
                this.getApiEndpoint(),
                {
                    token: this.token,
                    operation: S3Operation.UPLOAD,
                    filename: file.name,
                    contentType: file.type,
                },
            );

            const { url, fileId, s3Url } = response.data;

            if (!url || !fileId || !s3Url) {
                console.error('Missing data in presigned URL response');
                return undefined;
            }

            // Upload the file using the presigned URL
            await axios.put(url, file, {
                headers: {
                    'Content-Type': file.type,
                },
            });

            // Return file info
            return {
                id: fileId,
                name: file.name,
                size: file.size,
                url: s3Url,
            };
        } catch (err) {
            console.error(`Error while uploading file ${file.name}:`, err);
            return undefined;
        }
    }

    async deleteFile(fileId: string): Promise<boolean> {
        this.checkToken();
        console.debug(`Getting presigned URL for delete: ${fileId}`);

        try {
            // Get presigned URL for delete
            const response = await axios.post<PresignedUrlResponse>(
                this.getApiEndpoint(),
                {
                    token: this.token,
                    operation: S3Operation.DELETE,
                    fileId,
                },
            );

            const { url } = response.data;

            if (!url) {
                console.error('Missing URL in presigned URL response');
                return false;
            }

            // Delete the file using the presigned URL
            await axios.delete(url);

            return true;
        } catch (err) {
            console.error(`Error while deleting file ${fileId}:`, err);
            return false;
        }
    }

    async getFile(fileUrl: string): Promise<AwsFile | undefined> {
        this.checkToken();
        const id = splitAtLastOccurrence(fileUrl, '/')[1];
        console.debug(`Getting file info: ${id}`);

        try {
            // Get presigned URL for download
            const response = await axios.get<PresignedUrlResponse>(
                this.getApiEndpoint(),
                {
                    params: {
                        token: this.token,
                        operation: S3Operation.DOWNLOAD,
                        fileId: id,
                    },
                },
            );

            const { url } = response.data;

            if (!url) {
                console.error('Missing URL in presigned URL response');
                return undefined;
            }

            // Fetch the file metadata
            const fileResponse = await axios.get(url, {
                responseType: 'blob',
            });

            const filename = (fileResponse.headers['x-amz-meta-filename'] ??
                id) as string;
            const size = parseInt(
                (fileResponse.headers['x-amz-meta-size'] ?? '0') as string,
            );

            return {
                id,
                name: filename,
                size: size,
                url: fileUrl,
            };
        } catch (err) {
            console.error(`Error while getting file ${id}:`, err);
            return undefined;
        }
    }
}

// We don't export the instance directly, since we need to set the token first
export const createAwsS3PresignedClient = () => new AwsS3PresignedClient();
